import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { COMPANY } from "../mock";

const SECTIONS = [
  ["1. About these terms", [
    `These Web Design, Development & Ownership Terms ("Terms") apply to every website, online shop and web development project ("Project") carried out by ${COMPANY.name} ("we", "us", "our") for you, the client ("you", "your").`,
    "By accepting a quotation, paying a deposit or asking us to start work, you agree to these Terms. They sit alongside our general Terms & Conditions; if the two conflict on a web design matter, these Terms apply.",
  ]],
  ["2. Quotations & scope", [
    "Every Project is based on a written quotation that sets out the pages, features and price agreed. Quotations are valid for 30 days.",
    "Any work outside the agreed scope — such as extra pages, new features or major changes after a design has been approved — will be quoted separately before we carry it out.",
  ]],
  ["3. Payment terms", [
    "A non-refundable deposit of 50% of the Project price is due before any work begins. This secures your place in our schedule and covers initial design work.",
    "The remaining 50% balance is due on completion, before the website is launched or handed over. Invoices are payable within 7 days of issue.",
    "Ongoing services such as hosting, maintenance or social media management are billed separately at the agreed rate.",
  ]],
  ["4. Ownership until full payment", [
    "The website, its design, code, graphics, written content we produce and all related files remain the sole property of " + COMPANY.name + " until full payment of all amounts due for the Project has been received.",
    "Until full payment is received, you are granted a limited, revocable licence to view and review the website only. You may not copy, transfer, resell or move the website or its files to another provider.",
  ]],
  ["5. Transfer of ownership", [
    "Once full payment has been received, ownership of the finished website design and the content we have created specifically for you passes to you.",
    "Third-party items — such as themes, plugins, fonts, stock images and software licences — remain owned by their creators and are licensed for use on your website under their own terms.",
    "We keep ownership of our own general tools, code libraries and know-how used to build the website, and grant you a permanent licence to use them as part of your site.",
    "Unless you ask us not to, we may show the finished website in our portfolio and include a small credit link in the website footer.",
  ]],
  ["6. Late or non-payment", [
    "If any invoice is more than 14 days overdue, we may pause all work on the Project until payment is received.",
    "If any invoice is more than 30 days overdue, we may take the website offline, suspend hosting and withdraw your licence to use the website. We will give you at least 7 days' written notice before doing so.",
    "A website taken offline for non-payment will be restored once all outstanding amounts have been paid. A reconnection fee may apply.",
    "We reserve the right to charge interest and recovery costs on late payments as permitted under the Late Payment of Commercial Debts (Interest) Act 1998.",
  ]],
  ["7. Your responsibilities", [
    "You agree to supply text, images, logos and any other materials we need in good time. Delays in providing content may delay your launch date.",
    "You confirm that you own or have permission to use any materials you give us, and that they do not break any law or anyone else's rights.",
    "You are responsible for checking and approving each stage of the Project. Once you approve a design or the finished website, further changes may be charged as extra work.",
  ]],
  ["8. Revisions & sign-off", [
    "Each package includes a reasonable number of revisions at the design stage, as set out in your quotation. Additional rounds of changes may be charged.",
    "The Project is treated as complete when the website is launched or when you approve the finished site, whichever comes first.",
  ]],
  ["9. Domains, hosting & third-party services", [
    "Where we register a domain name or arrange hosting on your behalf, it will be registered in your name (or transferred to you) once full payment has been received.",
    "We are not responsible for outages, changes or price increases made by third-party hosting, domain, payment or software providers.",
  ]],
  ["10. Cancellation", [
    "If you cancel the Project after work has started, the deposit is non-refundable and you will be charged for any work completed beyond the value of the deposit.",
    "Any work, files or designs produced before cancellation remain our property unless all amounts due have been paid in full.",
  ]],
  ["11. Liability", [
    "We take great care building every website, but we cannot guarantee it will be completely error-free or achieve any particular search ranking, traffic or sales.",
    "Our total liability for any Project is limited to the amount you have paid for that Project. We are not liable for any indirect loss, including loss of profit, business or data.",
    "Nothing in these Terms limits liability that cannot be limited by law.",
  ]],
  ["12. Governing law", [
    "These Terms are governed by the law of Scotland, and any disputes will be dealt with by the Scottish courts.",
  ]],
];

export default function WebDesignTerms() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.title;
    document.title = `Web Design, Development & Ownership Terms | ${COMPANY.name}`;
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="relative bg-white text-slate-900">
      <Navbar />
      <main className="pt-32 pb-24" data-testid="web-design-terms-page">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="mt-8">
            <span className="section-eyebrow">Legal</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
              Web Design, Development &amp; Ownership Terms
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed">
              These terms explain how we work on website and web development
              projects, how payment works and who owns the website.
            </p>
            <div data-testid="web-terms-ownership-callout" className="mt-6 p-5 rounded-2xl bg-sky-50 border border-sky-100 text-sm text-slate-700 leading-relaxed">
              <strong className="text-slate-900">In short:</strong> a 50% deposit is due before work starts and the
              balance on completion. The website remains the property of {COMPANY.name} until full payment has been
              received.
            </div>
            <p className="mt-4 text-xs text-slate-500">Last updated: June 2026</p>
          </div>
          <div className="mt-12 space-y-10">
            {SECTIONS.map(([title, paras]) => (
              <section key={title}>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">{title}</h2>
                <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-slate-700">
                  {paras.map((p) => <p key={p}>{p}</p>)}
                </div>
              </section>
            ))}
          </div>
          <div className="mt-16 p-6 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-slate-600">
            Questions about these terms? Email{" "}
            <a href={`mailto:${COMPANY.email}`} className="underline">{COMPANY.email}</a>. See also our{" "}
            <Link to="/terms" className="underline">general Terms &amp; Conditions</Link>.
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
